import { Check } from "lucide-react";
import { COUNTRIES, EXPERIENCE, GOALS, TECHNIQUES } from "@/lib/constants";
import { Field, fieldCls, labelCls } from "@/components/FormBits";
import { Combobox } from "@/components/Combobox";
import { Input } from "@/components/ui/input";

const COUNTRY_OPTS = COUNTRIES.map((c) => ({ value: c, label: c }));

export const emptyPractice = { preferred_name: "", studio_name: "", country: "", region: "", experience: "", techniques: [] };

export function SelectCard({ selected, onClick, children, testId, multi = true }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId} role={multi ? "checkbox" : "radio"} aria-checked={selected}
      className={`flex min-h-[52px] w-full items-center gap-3 rounded-2xl border px-4 py-3 text-left transition-colors duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#C5A059] ${
        selected ? "border-[#2C1810] bg-[#2C1810] text-[#FDFBF7]" : "border-[#E6DBCF] bg-white/70 text-[#2C1810] hover:border-[#D99B90] hover:bg-[#FDF2F0]"
      }`}>
      <span className={`flex h-5 w-5 shrink-0 items-center justify-center ${multi ? "rounded-md" : "rounded-full"} border ${selected ? "border-[#C5A059] bg-[#C5A059] text-[#2C1810]" : "border-[#A88B7D]"}`}>
        {selected && <Check size={13} strokeWidth={3} />}
      </span>
      <span className="flex-1">{children}</span>
    </button>
  );
}

const toggle = (list, v) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export function PracticeFields({ value, onChange }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="space-y-6">
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="First or preferred name *" id="practice-name-input" autoComplete="given-name" value={value.preferred_name} onChange={(e) => set("preferred_name", e.target.value)} maxLength={60} />
        <Field label="Business or studio name (optional)" id="practice-studio-input" autoComplete="organization" value={value.studio_name} onChange={(e) => set("studio_name", e.target.value)} maxLength={100} />
        <div>
          <span className={labelCls}>Country *</span>
          <div className="mt-1.5">
            <Combobox options={COUNTRY_OPTS} value={value.country} onChange={(v) => set("country", v)} placeholder="Choose your country" searchPlaceholder="Search countries…" testId="practice-country-select" />
          </div>
        </div>
        <div>
          <label htmlFor="practice-region-input" className={labelCls}>State, province or region</label>
          <Input id="practice-region-input" data-testid="practice-region-input" value={value.region} onChange={(e) => set("region", e.target.value)} maxLength={80} className={`mt-1.5 ${fieldCls}`} />
        </div>
      </div>
      <fieldset>
        <legend className={labelCls}>Experience level *</legend>
        <div className="mt-2 grid gap-2 sm:grid-cols-2" role="radiogroup">
          {EXPERIENCE.map((e) => (
            <SelectCard key={e} multi={false} selected={value.experience === e} onClick={() => set("experience", e)} testId={`practice-exp-${slug(e)}`}>{e}</SelectCard>
          ))}
        </div>
      </fieldset>
      <fieldset>
        <legend className={labelCls}>Techniques practiced or currently studied * <span className="normal-case tracking-normal text-[#A88B7D]">— select all that apply</span></legend>
        <div className="mt-2 grid gap-2">
          {TECHNIQUES.map((t) => (
            <SelectCard key={t.value} selected={value.techniques.includes(t.value)} onClick={() => set("techniques", toggle(value.techniques, t.value))} testId={`practice-tech-${slug(t.value)}`}>
              <span className="block text-sm font-semibold">{t.value}</span>
              <span className="block text-xs opacity-75">{t.hint}</span>
            </SelectCard>
          ))}
        </div>
      </fieldset>
    </div>
  );
}

export const practiceError = (p) =>
  !p.preferred_name.trim() ? "Please enter your first or preferred name."
    : !p.country ? "Please choose your country."
      : !p.experience ? "Please choose your experience level."
        : !p.techniques.length ? "Please select at least one technique." : "";

export function GoalsFields({ value, onChange }) {
  return (
    <div className="grid gap-2" data-testid="goals-list">
      {GOALS.map((g) => (
        <SelectCard key={g} selected={value.includes(g)} onClick={() => onChange(toggle(value, g))} testId={`goal-${slug(g)}`}>
          <span className="text-sm font-medium">{g}</span>
        </SelectCard>
      ))}
    </div>
  );
}
